import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import ReactBadge from "react-bootstrap/Badge";
import { MdInfoOutline } from "react-icons/md";
import { labelForCallGroupType, labelForOutcomeTag } from "./aiCallLogConstants";
import AiCallLogDetailsModal from "./AiCallLogDetailsModal";
import { formatAiCallLogInstant, outboundStatusVariant } from "./aiCallLogColumnFormatters";

const WrappedBadge = (props) => (
  <div>
    <ReactBadge {...props}>{props.children}</ReactBadge>
  </div>
);

const PillBadge = ({ children, bg }) => (
  <ReactBadge pill bg={bg} className="fw-normal">
    {children}
  </ReactBadge>
);

/** Comma-separated switch info IDs (`AiCallLogRowDto.switchInfoIds`). */
export function AiCallLogSwitchesCell({ value }) {
  if (!value?.length) {
    return <span className="text-muted">—</span>;
  }
  const ids = Array.isArray(value) ? value : [];
  const text = ids
    .map((id) => (id == null || id === "" ? "" : String(id)))
    .filter((s) => s !== "")
    .join(", ");
  if (!text) {
    return <span className="text-muted">—</span>;
  }
  return (
    <div
      style={{
        minWidth: 0,
        maxWidth: "100%",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        lineHeight: 1.35,
        textAlign: "left",
      }}
    >
      {text}
    </div>
  );
}

function normalizeOutcomeTags(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return [...value].filter(Boolean).sort();
  if (typeof value === "object" && value !== null && Symbol.iterator in value) {
    try {
      return [...value].filter(Boolean).sort();
    } catch {
      return [];
    }
  }
  return [];
}

export function AiCallLogOutcomeTagsCell({ value }) {
  const tags = normalizeOutcomeTags(value);
  if (!tags.length) {
    return <span className="text-muted">—</span>;
  }
  return (
    <div className="d-flex flex-wrap gap-1 align-items-center">
      {tags.map((raw, i) => (
        <PillBadge key={`${raw}-${i}`} bg="dark">
          {labelForOutcomeTag(raw)}
        </PillBadge>
      ))}
    </div>
  );
}

export function AiCallLogStringListCell({ value, badgeBg = "secondary" }) {
  if (!value?.length) {
    return <span className="text-muted">—</span>;
  }
  return (
    <div className="d-flex flex-wrap gap-1 align-items-center">
      {value.map((item, i) => (
        <PillBadge key={`${item}-${i}`} bg={badgeBg}>
          {item}
        </PillBadge>
      ))}
    </div>
  );
}

export function AiCallLogCallGroupTypeCell({ value }) {
  return (
    <WrappedBadge pill variant="secondary">
      {labelForCallGroupType(value)}
    </WrappedBadge>
  );
}

export function AiCallLogOutboundStatusCell({ value }) {
  return (
    <WrappedBadge pill variant={outboundStatusVariant(value)}>
      {value}
    </WrappedBadge>
  );
}

/** Info icon opens call details modal (Listen, Call Group, AI outreach block). */
export function AiCallLogInfoCell({ row }) {
  const [show, setShow] = useState(false);
  const original = row?.original || {};

  return (
    <>
      <Button
        size="sm"
        variant="link"
        className="p-0"
        title="Call details"
        aria-label="Call details"
        data-testid="ai-call-log-info-btn"
        onClick={() => setShow(true)}
      >
        <MdInfoOutline size={22} />
      </Button>
      <AiCallLogDetailsModal
        show={show}
        onHide={() => setShow(false)}
        outboundPhoneNumber={original.outboundPhoneNumber}
        aiCallSid={original.aiCallSid ?? original.polyaiCallSid}
        callGroupUuid={original.callGroupUuid}
      />
    </>
  );
}

export function AiCallLogDateTimeCell({ value }) {
  return <span>{formatAiCallLogInstant(value)}</span>;
}
